var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Divider, FlexBox, Modal, ModalBody, ModalFoot, ModalHead, Text, } from '@dixa/lib-shared-habla';
import { useCallback, useState } from 'react';
import { ajaxSend } from 'api/api';
import { QueueSelect } from 'components/queueSelect';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { FacebookLoginButton } from '../facebookLoginButton';
import styles from './facebookAuthenticator.module.scss';
/** A component that displays a facebook login modal and it's associated modal. */
export const FacebookAuthenticator = ({ appId, facebookButtonProps, children, onSuccess, }) => {
    const [status, setStatus] = useState('uninitialized');
    const [prerequisitesModalOpen, setPrerequisitesModalOpen] = useState(false);
    const [selectQueueModalOpen, setSelectQueueModalOpen] = useState(false);
    const [queueId, setQueueId] = useState();
    const [facebookAuthenticationData, setFacebookAuthenticationData] = useState();
    const organizationId = useAppSelector((state) => state.settings.organization.organization.id);
    /** Resets the modal. */
    const resetModal = useCallback(() => {
        setStatus('uninitialized');
        setQueueId(undefined);
        setFacebookAuthenticationData(undefined);
        setSelectQueueModalOpen(false);
        setPrerequisitesModalOpen(false);
    }, []);
    /** Handles the facebook authentication. */
    const handleFacebookAuthenticated = useCallback((data) => {
        setFacebookAuthenticationData(data);
        setSelectQueueModalOpen(true);
    }, []);
    /** Handles confirming the queue. */
    const handleQueueConfirm = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        setStatus('pending');
        try {
            yield ajaxSend({
                path: '/v1/facebook-messenger/connect',
                method: 'PUT',
                data: {
                    default_queue_id: queueId,
                    organization_id: organizationId,
                    application_id: appId,
                    user_id: facebookAuthenticationData === null || facebookAuthenticationData === void 0 ? void 0 : facebookAuthenticationData.userId,
                    user_access_token: facebookAuthenticationData === null || facebookAuthenticationData === void 0 ? void 0 : facebookAuthenticationData.accessToken,
                    connect_facebook: true,
                    connect_instagram: false,
                },
            });
            resetModal();
            onSuccess();
        }
        catch (error) {
            setStatus('rejected');
        }
    }), [
        appId,
        facebookAuthenticationData === null || facebookAuthenticationData === void 0 ? void 0 : facebookAuthenticationData.accessToken,
        facebookAuthenticationData === null || facebookAuthenticationData === void 0 ? void 0 : facebookAuthenticationData.userId,
        onSuccess,
        organizationId,
        queueId,
        resetModal,
    ]);
    /** The authentication prerequisites. */
    const prerequisites = [
        'An existing created Facebook page.',
        'A Facebook user with owner or admin role on the target pages.',
    ];
    /**
     * This are tips that can help admins understand how the integration works and
     * help them not mess it up.
     */
    const tips = [
        'Every time a user connects Facebook pages, their previous granted authentication is invalidated. Bear this in mind when you have to create more than one page, as you should not add them one by one, but all at once.',
        'Changing the Facebook password or other important settings might cause the connection to be invalidated, control the connection state by checking that you still receive messages, and reconnect the page if necessary.',
    ];
    return (_jsxs(_Fragment, { children: [_jsx(Button, Object.assign({ onClick: () => setPrerequisitesModalOpen(true) }, facebookButtonProps, { children: children })), _jsxs(Modal, Object.assign({ isOpen: prerequisitesModalOpen, className: styles.modal, onClose: resetModal }, { children: [_jsx(ModalHead, { title: "Facebook for business", onClose: resetModal }), _jsx(ModalBody, { children: _jsxs(FlexBox, Object.assign({ gap: '300', flexDirection: "column" }, { children: [_jsx(Text.Paragraph, { children: "Following the connection of a page, all Facebook messages will be seamlessly routed to Dixa, while agent responses will also be displayed on Facebook." }), _jsx(Divider, { size: "xSmall" }), _jsx(Text.Paragraph, { children: 'What you need' }), _jsx("ul", { children: prerequisites.map((text, index) => (_jsx("li", { children: _jsx(Text.Paragraph, { children: text }) }, index))) }), _jsx(Text.Paragraph, { children: 'Also good to know' }), _jsx("ul", { children: tips.map((text, index) => (_jsx("li", { children: _jsx(Text.Paragraph, { children: text }) }, index))) })] })) }), _jsxs(FlexBox, Object.assign({ justifyContent: "flex-end", gap: "300", padding: "500" }, { children: [_jsx(Button, Object.assign({ variant: "tertiary", onClick: resetModal }, { children: "Cancel" })), _jsx(FacebookLoginButton, Object.assign({ scope: "pages_show_list,pages_manage_metadata,pages_messaging,pages_read_engagement", onSuccess: handleFacebookAuthenticated, platform: "facebook" }, { children: "Continue" }))] }))] })), _jsxs(Modal, Object.assign({ isOpen: selectQueueModalOpen, className: styles.modal, onClose: resetModal }, { children: [_jsx(ModalHead, { title: "Facebook for business", onClose: resetModal }), _jsx(ModalBody, { children: _jsx(QueueSelect, { onSelect: (queue) => setQueueId(queue.id) }) }), _jsx(ModalFoot, { confirmName: "Continue", confirmOnClick: handleQueueConfirm, confirmProps: {
                            disabled: queueId === undefined,
                            isLoading: status === 'pending',
                        }, cancelOnClick: resetModal })] }))] }));
};
